class OverlayAnimation extends HTMLElement {
  static FIREWORK_DENSITY = 6;
  static PARTICLES_PER_ROCKET = 45;
  static FIRE_COLORS = ["#ff3b3b", "#ffd93d", "#4aeaff", "#9d4bff", "#ffffff"];

  static SNOW_STYLES = {
    light: {
      count: 60,
      sizeMin: 0.5,
      sizeMax: 1,
      speedMin: 0.2,
      speedMax: 1,
      windMin: -0.05,
      windMax: 0.05,
      twinkle: false
    },
    twinkle: {
      count: 150,
      sizeMin: 1,
      sizeMax: 3,
      speedMin: 0.5,
      speedMax: 1.3,
      windMin: -0.2,
      windMax: 0.2,
      twinkle: true
    }
  };

  static SNOW_STYLE = "light";
  static BASE_AREA = 1440 * 900;

  constructor() {
    super();
    this.attachShadow({mode: "open"});

    this.canvas = document.createElement("canvas");
    this.canvas.id = "overlay-animation-canvas";
    const style = this.canvas.style;
    style.position = "fixed";
    style.inset = "0";
    style.width = "100vw";
    style.height = "100vh";
    style.pointerEvents = "none";
    style.zIndex = "999999";

    this.shadowRoot.appendChild(this.canvas);
    this.ctx = this.canvas.getContext("2d");
    this.DPR = window.devicePixelRatio || 1;

    this.width = 0;
    this.height = 0;
    this.rockets = [];
    this.particles = [];
    this.flakes = [];

    this._resizeHandler = () => this._resize();

    this.mode = this.dataset.animation;
    this.timeout = +(this.dataset.timeout || 0) * 1000;
    this.snowSpeed = 1;
    this.fireworkSpeed = 1;
    this.sakuraSpeed = 1;
    this.heartSpeed = 1;
    this.roseSpeed = 1;
    this.hydrangeaSpeed = 1;
    this.margueriteSpeed = 1;
    this.autumnLeavesSpeed = 1;
    this.halloweenSpeed = 1;
    this.animatingStarted = false;

    this.snowBitmap = null;
    this.sakuraBitmap = null;
    this.heartBitmap = null;
    this.roseBitmap = null;
    this.hydrangeaBitmap = null;
    this.margueriteBitmap = null;
    this.autumnLeavesBitmap = null;
    this.halloweenBitmap = null;
  }

  static rand(a, b) {
    return Math.random() * (b - a) + a;
  }

  static fireColor() {
    const COLORS = OverlayAnimation.FIRE_COLORS;
    return COLORS[Math.floor(OverlayAnimation.rand(0, COLORS.length))];
  }

  connectedCallback() {
    this.mode = this.dataset.animation || this.mode || "none";
    this.timeout = +(this.dataset.timeout || 0) * 1000;
    const {snowSpeed, fireworksSpeed, sakuraSpeed, heartSpeed, roseSpeed, hydrangeaSpeed, margueriteSpeed, autumnLeavesSpeed, halloweenSpeed} = this.dataset;

    this.snowSpeed = snowSpeed !== undefined ? parseFloat(snowSpeed) || 1 : 1;
    this.fireworkSpeed = fireworksSpeed !== undefined ? parseFloat(fireworksSpeed) || 1 : 1;
    this.sakuraSpeed = sakuraSpeed !== undefined ? parseFloat(sakuraSpeed) || 1 : 1;
    this.heartSpeed = heartSpeed !== undefined ? parseFloat(heartSpeed) || 0.3 : 0.3;
    this.roseSpeed = roseSpeed !== undefined ? parseFloat(roseSpeed) || 1 : 1;
    this.hydrangeaSpeed = hydrangeaSpeed !== undefined ? parseFloat(hydrangeaSpeed) || 1 : 1;
    this.margueriteSpeed = margueriteSpeed !== undefined ? parseFloat(margueriteSpeed) || 1 : 1;
    this.autumnLeavesSpeed = autumnLeavesSpeed !== undefined ? parseFloat(autumnLeavesSpeed) || 1 : 1;
    this.halloweenSpeed = halloweenSpeed !== undefined ? parseFloat(halloweenSpeed) || 1 : 1;

    this.snowSpeed = Math.min(Math.max(this.snowSpeed, 0.2), 3);
    this.fireworkSpeed = Math.min(Math.max(this.fireworkSpeed, 0.5), 2);
    this.sakuraSpeed = Math.min(Math.max(this.sakuraSpeed, 0.2), 3);
    this.heartSpeed = Math.min(Math.max(this.heartSpeed, 0.1), 2);
    this.roseSpeed = Math.min(Math.max(this.roseSpeed, 0.2), 3);
    this.hydrangeaSpeed = Math.min(Math.max(this.hydrangeaSpeed, 0.2), 3);
    this.margueriteSpeed = Math.min(Math.max(this.margueriteSpeed, 0.2), 3);
    this.autumnLeavesSpeed = Math.min(Math.max(this.autumnLeavesSpeed, 0.2), 3);
    this.halloweenSpeed = Math.min(Math.max(this.halloweenSpeed, 0.2), 3);

    if (this.mode === "none") return;

    this._resize();
    window.addEventListener("resize", this._resizeHandler, {passive: true});

    if (this.mode === "snow") {
      this._createSnowBitmap();
    } else if (this.mode === "sakura") {
      this._createSakuraBitmap();
    } else if (this.mode === "heart") {
      this._createHeartBitmap();
    } else if (this.mode === "rose") {
      this._createRoseBitmap();
    } else if (this.mode === "hydrangea") {
      this._createHydrangeaBitmap();
    } else if (this.mode === "marguerite") {
      this._createMargueriteBitmap();
    } else if (this.mode === "autumn_leaves") {
      this._createAutumnLeavesBitmap();
    } else if (this.mode === "halloween") {
      this._createHalloweenBitmap();
    }

    this.drawInitialFrame();

    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(() => this.startAnimationAfterRender(), {timeout: 1000});
    } else {
      setTimeout(() => this.startAnimationAfterRender(), 0);
    }
  }

  startAnimationAfterRender() {
    if (this.animatingStarted) return;
    this.animatingStarted = true;
    this.animate();

    if (this.timeout > 0) {
      setTimeout(() => this.remove(), this.timeout);
    }
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this._resizeHandler);
  }

  _resize() {
    const {innerWidth, innerHeight} = window;
    this.width = innerWidth;
    this.height = innerHeight;

    this.canvas.width = this.width * this.DPR;
    this.canvas.height = this.height * this.DPR;
    this.canvas.style.width = `${this.width}px`;
    this.canvas.style.height = `${this.height}px`;

    this.ctx.setTransform(this.DPR, 0, 0, this.DPR, 0, 0);

    if (this.mode === "snow" || this.mode === "sakura" || this.mode === "heart" || 
        this.mode === "rose" || this.mode === "hydrangea" || this.mode === "marguerite" || 
        this.mode === "autumn_leaves" || this.mode === "halloween") {
      this.flakes.length = 0;
    }
  }

  spawnRocket() {
    const rockets = this.rockets;
    const width = this.width, height = this.height;
    for (let i = 0; i < OverlayAnimation.FIREWORK_DENSITY; i++) {
      rockets.push({
        x: OverlayAnimation.rand(0, width),
        y: height + 10,
        vx: OverlayAnimation.rand(-1, 1),
        vy: OverlayAnimation.rand(-14, -8),
        ay: 0.14,
        far: OverlayAnimation.rand(height * 0.2, height * 0.55),
        color: OverlayAnimation.fireColor()
      });
    }
  }

  explode(x, y, color) {
    const particles = this.particles;
    for (let i = 0; i < OverlayAnimation.PARTICLES_PER_ROCKET; i++) {
      particles.push({
        x,
        y,
        vx: OverlayAnimation.rand(-4, 4),
        vy: OverlayAnimation.rand(-4, 4),
        ay: 0.06,
        alpha: 1,
        color,
        size: OverlayAnimation.rand(1.4, 3.4),
        life: OverlayAnimation.rand(35, 65)
      });
    }
  }

  get snowOpts() {
    return OverlayAnimation.SNOW_STYLES[OverlayAnimation.SNOW_STYLE];
  }

  _randSnowSize() {
    const r = Math.random();
    if (r < 0.6) {
      return 8 + Math.random() * 10;
    } else {
      return 18 + Math.random() * 14;
    }
  }

  _randSakuraSize() {
    const r = Math.random();
    if (r < 0.3) {
      return 18 + Math.random() * 10;
    } else {
      return 24 + Math.random() * 16;
    }
  }

  _randHeartSize() {
    const r = Math.random();
    if (r < 0.4) {
      return 10 + Math.random() * 10;
    } else {
      return 20 + Math.random() * 15;
    }
  }

  _randRoseSize() {
    const r = Math.random();
    if (r < 0.4) {
      return 15 + Math.random() * 10;
    } else {
      return 25 + Math.random() * 15;
    }
  }

  _randHydrangeaSize() {
    const r = Math.random();
    if (r < 0.5) {
      return 20 + Math.random() * 15;
    } else {
      return 35 + Math.random() * 20;
    }
  }

  _randMargueriteSize() {
    const r = Math.random();
    if (r < 0.4) {
      return 18 + Math.random() * 12;
    } else {
      return 30 + Math.random() * 15;
    }
  }

  _randAutumnLeavesSize() {
    const r = Math.random();
    if (r < 0.3) {
      return 15 + Math.random() * 10;
    } else {
      return 25 + Math.random() * 20;
    }
  }

  _randHalloweenSize() {
    const r = Math.random();
    if (r < 0.5) {
      return 20 + Math.random() * 15;
    } else {
      return 35 + Math.random() * 20;
    }
  }
  _createSnowBitmap() {
    const size = 64;
    const off = document.createElement("canvas");
    off.width = size;
    off.height = size;
    const c = off.getContext("2d");

    const r = size / 2;
    c.translate(r, r);

    c.clearRect(-r, -r, size, size);

    c.shadowBlur = 10;
    c.shadowColor = "rgba(255, 255, 255, 0.9)";
    c.strokeStyle = "#ffffff";
    c.lineWidth = 2;
    c.lineCap = "round";
    c.lineJoin = "round";

    const armLength = r - 6;
    const branchLen = armLength * 0.35;
    const branchAngle = Math.PI / 6;
    const arms = 6;

    for (let i = 0; i < arms; i++) {
      c.save();
      c.rotate((i * 2 * Math.PI) / arms);

      c.beginPath();
      c.moveTo(0, 0);
      c.lineTo(armLength, 0);
      c.stroke();

      const p1 = armLength * 0.4;
      const p2 = armLength * 0.75;

      [p1, p2].forEach((p) => {
        c.beginPath();
        c.moveTo(p, 0);
        c.lineTo(
          p + branchLen * Math.cos(branchAngle),
          branchLen * Math.sin(branchAngle)
        );
        c.stroke();

        c.beginPath();
        c.moveTo(p, 0);
        c.lineTo(
          p + branchLen * Math.cos(branchAngle),
          -branchLen * Math.sin(branchAngle)
        );
        c.stroke();
      });

      c.beginPath();
      c.arc(armLength, 0, 2.2, 0, Math.PI * 2);
      c.stroke();

      c.restore();
    }

    this.snowBitmap = off;
  }

  _createSakuraBitmap() {
    const size = 64;
    const off = document.createElement("canvas");
    off.width = size;
    off.height = size;
    const c = off.getContext("2d");

    const centerX = size / 2;
    const centerY = size / 2;

    c.clearRect(0, 0, size, size);

    const petalLength = size * 0.7;
    const petalWidth = size * 0.45;
    
    c.save();
    c.translate(centerX, centerY);

    c.shadowBlur = 12;
    c.shadowColor = "rgba(255, 182, 193, 0.6)";

    const gradient = c.createLinearGradient(0, -petalLength / 2, 0, petalLength / 2);
    gradient.addColorStop(0, "#ffe0e8");
    gradient.addColorStop(0.2, "#ffd0dc");
    gradient.addColorStop(0.5, "#ffc0cb");
    gradient.addColorStop(0.8, "#ffd0dc");
    gradient.addColorStop(1, "rgba(255, 240, 245, 0.9)");

    c.fillStyle = gradient;
    c.beginPath();
    
    c.moveTo(0, petalLength / 2);
    
    c.bezierCurveTo(
      -petalWidth * 0.4, petalLength * 0.2,
      -petalWidth * 0.55, -petalLength * 0.1,
      -petalWidth * 0.35, -petalLength * 0.4
    );
    
    c.lineTo(0, -petalLength * 0.35);
    c.lineTo(petalWidth * 0.35, -petalLength * 0.4);
    
    c.bezierCurveTo(
      petalWidth * 0.55, -petalLength * 0.1,
      petalWidth * 0.4, petalLength * 0.2,
      0, petalLength / 2
    );
    
    c.closePath();
    c.fill();

    c.shadowBlur = 0;
    c.strokeStyle = "rgba(255, 182, 193, 0.35)";
    c.lineWidth = 0.6;
    
    c.beginPath();
    c.moveTo(0, petalLength / 2);
    c.lineTo(0, -petalLength * 0.3);
    c.stroke();
    
    c.beginPath();
    c.moveTo(-petalWidth * 0.08, petalLength * 0.35);
    c.quadraticCurveTo(
      -petalWidth * 0.15, 0,
      -petalWidth * 0.2, -petalLength * 0.25
    );
    c.stroke();
    
    c.beginPath();
    c.moveTo(petalWidth * 0.08, petalLength * 0.35);
    c.quadraticCurveTo(
      petalWidth * 0.15, 0,
      petalWidth * 0.2, -petalLength * 0.25
    );
    c.stroke();

    const highlightGradient = c.createRadialGradient(
      -petalWidth * 0.1, -petalLength * 0.15, 0,
      -petalWidth * 0.1, -petalLength * 0.15, petalLength * 0.4
    );
    highlightGradient.addColorStop(0, "rgba(255, 255, 255, 0.7)");
    highlightGradient.addColorStop(0.5, "rgba(255, 255, 255, 0.3)");
    highlightGradient.addColorStop(1, "rgba(255, 255, 255, 0)");
    
    c.fillStyle = highlightGradient;
    c.beginPath();
    c.moveTo(0, petalLength / 2);
    c.bezierCurveTo(
      -petalWidth * 0.4, petalLength * 0.2,
      -petalWidth * 0.55, -petalLength * 0.1,
      -petalWidth * 0.35, -petalLength * 0.4
    );
    c.lineTo(0, -petalLength * 0.25);
    c.lineTo(petalWidth * 0.35, -petalLength * 0.4);
    c.bezierCurveTo(
      petalWidth * 0.55, -petalLength * 0.1,
      petalWidth * 0.4, petalLength * 0.2,
      0, petalLength / 2
    );
    c.closePath();
    c.fill();

    c.shadowBlur = 0;
    
    for (let i = 0; i < 3; i++) {
      const sparkleX = (Math.random() - 0.5) * petalWidth * 0.6;
      const sparkleY = (Math.random() - 0.5) * petalLength * 0.6;
      
      c.save();
      c.translate(sparkleX, sparkleY);
      c.rotate(Math.random() * Math.PI);
      
      const sparkleGradient = c.createRadialGradient(0, 0, 0, 0, 0, 3);
      sparkleGradient.addColorStop(0, "rgba(255, 255, 255, 0.9)");
      sparkleGradient.addColorStop(0.5, "rgba(255, 240, 245, 0.6)");
      sparkleGradient.addColorStop(1, "rgba(255, 255, 255, 0)");
      
      c.fillStyle = sparkleGradient;
      c.beginPath();
      for (let j = 0; j < 4; j++) {
        const angle = (j * Math.PI) / 2;
        const x = Math.cos(angle) * 2.5;
        const y = Math.sin(angle) * 2.5;
        if (j === 0) c.moveTo(x, y);
        else c.lineTo(x, y);
      }
      c.closePath();
      c.fill();
      
      c.restore();
    }

    c.restore();

    this.sakuraBitmap = off;
  }

  _createHeartBitmap() {
    const size = 64;
    const off = document.createElement("canvas");
    off.width = size;
    off.height = size;
    const c = off.getContext("2d");

    const centerX = size / 2;
    const centerY = size / 2;
    const heartSize = size * 0.42;

    c.clearRect(0, 0, size, size);

    c.globalAlpha = 0.7;  // 透明度を設定（0.0〜1.0）

    // ぼかしなし
    c.strokeStyle = "#ffcce6";
    c.lineWidth = 3;
    c.lineCap = "round";
    c.lineJoin = "round";

    c.beginPath();
    c.moveTo(centerX, centerY + heartSize * 0.9);

    c.bezierCurveTo(
      centerX - heartSize * 0.7, centerY + heartSize * 0.6,
      centerX - heartSize * 1.0, centerY - heartSize * 0.5,
      centerX, centerY - heartSize * 0.01
    );

    c.bezierCurveTo(
      centerX + heartSize * 1.0, centerY - heartSize * 0.5,
      centerX + heartSize * 0.7, centerY + heartSize * 0.6,
      centerX, centerY + heartSize * 0.9
    );

    c.stroke();

    this.heartBitmap = off;
  }



  _createRoseBitmap() {
    const size = 64;
    const off = document.createElement("canvas");
    off.width = size;
    off.height = size;
    const c = off.getContext("2d");

    const centerX = size / 2;
    const centerY = size / 2;

    c.clearRect(0, 0, size, size);

    const petalLength = size * 0.65;
    const petalWidth = size * 0.4;
    
    c.save();
    c.translate(centerX, centerY);

    c.shadowBlur = 10;
    c.shadowColor = "rgba(220, 20, 60, 0.5)";

    const gradient = c.createRadialGradient(0, 0, 0, 0, 0, petalLength / 2);
    gradient.addColorStop(0, "#ff1744");
    gradient.addColorStop(0.5, "#c2185b");
    gradient.addColorStop(1, "#880e4f");

    c.fillStyle = gradient;
    c.beginPath();
    
    c.moveTo(0, petalLength / 2);
    
    c.bezierCurveTo(
      -petalWidth * 0.5, petalLength * 0.3,
      -petalWidth * 0.6, -petalLength * 0.2,
      0, -petalLength * 0.4
    );
    
    c.bezierCurveTo(
      petalWidth * 0.6, -petalLength * 0.2,
      petalWidth * 0.5, petalLength * 0.3,
      0, petalLength / 2
    );
    
    c.closePath();
    c.fill();

    const highlightGradient = c.createRadialGradient(
      -petalWidth * 0.15, -petalLength * 0.1, 0,
      -petalWidth * 0.15, -petalLength * 0.1, petalLength * 0.35
    );
    highlightGradient.addColorStop(0, "rgba(255, 255, 255, 0.4)");
    highlightGradient.addColorStop(1, "rgba(255, 255, 255, 0)");
    
    c.fillStyle = highlightGradient;
    c.beginPath();
    c.arc(-petalWidth * 0.15, -petalLength * 0.1, petalLength * 0.25, 0, Math.PI * 2);
    c.fill();

    c.restore();

    this.roseBitmap = off;
  }

  _createHydrangeaBitmap() {
    const size = 64;
    const off = document.createElement("canvas");
    off.width = size;
    off.height = size;
    const c = off.getContext("2d");

    const centerX = size / 2;
    const centerY = size / 2;

    c.clearRect(0, 0, size, size);

    c.save();
    c.translate(centerX, centerY);

    const petalSize = size * 0.15;
    const positions = [
      {x: -petalSize * 0.8, y: -petalSize * 0.8},
      {x: petalSize * 0.8, y: -petalSize * 0.8},
      {x: -petalSize * 0.8, y: petalSize * 0.8},
      {x: petalSize * 0.8, y: petalSize * 0.8}
    ];

    positions.forEach(pos => {
      c.save();
      c.translate(pos.x, pos.y);

      const gradient = c.createRadialGradient(0, 0, 0, 0, 0, petalSize);
      gradient.addColorStop(0, "#e3f2fd");
      gradient.addColorStop(0.3, "#90caf9");
      gradient.addColorStop(0.7, "#5e35b1");
      gradient.addColorStop(1, "#4527a0");

      c.shadowBlur = 8;
      c.shadowColor = "rgba(94, 53, 177, 0.4)";

      for (let i = 0; i < 4; i++) {
        c.save();
        c.rotate((i * Math.PI) / 2);

        c.fillStyle = gradient;
        c.beginPath();
        c.moveTo(0, 0);
        c.bezierCurveTo(
          petalSize * 0.3, -petalSize * 0.2,
          petalSize * 0.5, -petalSize * 0.4,
          petalSize * 0.6, -petalSize * 0.6
        );
        c.bezierCurveTo(
          petalSize * 0.4, -petalSize * 0.5,
          petalSize * 0.2, -petalSize * 0.3,
          0, 0
        );
        c.closePath();
        c.fill();

        c.restore();
      }

      c.shadowBlur = 4;
      c.shadowColor = "rgba(255, 235, 59, 0.6)";
      c.fillStyle = "#ffeb3b";
      c.beginPath();
      c.arc(0, 0, petalSize * 0.15, 0, Math.PI * 2);
      c.fill();

      c.restore();
    });

    c.restore();

    this.hydrangeaBitmap = off;
  }

  _createMargueriteBitmap() {
    const size = 64;
    const off = document.createElement("canvas");
    off.width = size;
    off.height = size;
    const c = off.getContext("2d");

    const centerX = size / 2;
    const centerY = size / 2;

    c.clearRect(0, 0, size, size);

    c.save();
    c.translate(centerX, centerY);

    const petalLength = size * 0.35;
    const petalWidth = size * 0.12;
    const petalCount = 12;

    c.shadowBlur = 8;
    c.shadowColor = "rgba(255, 255, 255, 0.8)";

    for (let i = 0; i < petalCount; i++) {
      c.save();
      c.rotate((i * 2 * Math.PI) / petalCount);

      const gradient = c.createLinearGradient(0, 0, 0, petalLength);
      gradient.addColorStop(0, "#ffffff");
      gradient.addColorStop(0.7, "#f5f5f5");
      gradient.addColorStop(1, "#e0e0e0");

      c.fillStyle = gradient;
      c.beginPath();
      c.ellipse(0, petalLength * 0.6, petalWidth, petalLength * 0.6, 0, 0, Math.PI * 2);
      c.fill();

      c.restore();
    }

    c.shadowBlur = 6;
    c.shadowColor = "rgba(255, 193, 7, 0.6)";
    
    const centerGradient = c.createRadialGradient(0, 0, 0, 0, 0, size * 0.15);
    centerGradient.addColorStop(0, "#ffd54f");
    centerGradient.addColorStop(0.5, "#ffb300");
    centerGradient.addColorStop(1, "#ff8f00");

    c.fillStyle = centerGradient;
    c.beginPath();
    c.arc(0, 0, size * 0.15, 0, Math.PI * 2);
    c.fill();

    c.shadowBlur = 0;
    c.fillStyle = "rgba(255, 160, 0, 0.3)";
    for (let i = 0; i < 20; i++) {
      const angle = Math.random() * Math.PI * 2;
      const radius = Math.random() * size * 0.12;
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius;
      c.beginPath();
      c.arc(x, y, 1, 0, Math.PI * 2);
      c.fill();
    }

    c.restore();

    this.margueriteBitmap = off;
  }

  _createAutumnLeavesBitmap() {
    const size = 64;
    const off = document.createElement("canvas");
    off.width = size;
    off.height = size;
    const c = off.getContext("2d");

    const centerX = size / 2;
    const centerY = size / 2;

    c.clearRect(0, 0, size, size);

    c.save();
    c.translate(centerX, centerY);

    const leafLength = size * 0.7;
    const leafWidth = size * 0.4;

    const colors = ["#d84315", "#ff6f00", "#f57c00", "#ff8a65", "#bf360c"];
    const color = colors[Math.floor(Math.random() * colors.length)];

    c.shadowBlur = 8;
    c.shadowColor = "rgba(191, 54, 12, 0.4)";

    const gradient = c.createLinearGradient(-leafWidth / 2, 0, leafWidth / 2, 0);
    gradient.addColorStop(0, color);
    gradient.addColorStop(0.5, "#ffab40");
    gradient.addColorStop(1, color);

    c.fillStyle = gradient;
    c.beginPath();
    
    c.moveTo(0, -leafLength / 2);
    
    c.bezierCurveTo(
      leafWidth * 0.6, -leafLength * 0.2,
      leafWidth * 0.5, leafLength * 0.2,
      0, leafLength / 2
    );
    
    c.bezierCurveTo(
      -leafWidth * 0.5, leafLength * 0.2,
      -leafWidth * 0.6, -leafLength * 0.2,
      0, -leafLength / 2
    );
    
    c.closePath();
    c.fill();

    c.shadowBlur = 0;
    c.strokeStyle = "rgba(191, 54, 12, 0.6)";
    c.lineWidth = 1.5;
    c.beginPath();
    c.moveTo(0, -leafLength / 2);
    c.lineTo(0, leafLength / 2);
    c.stroke();

    for (let i = 0; i < 5; i++) {
      const y = -leafLength / 2 + (i * leafLength) / 5;
      c.beginPath();

      c.moveTo(0, y);
      c.lineTo(leafWidth * 0.3, y + leafLength * 0.08);
      c.stroke();
      
      c.beginPath();
      c.moveTo(0, y);
      c.lineTo(-leafWidth * 0.3, y + leafLength * 0.08);
      c.stroke();
    }

    c.restore();

    this.autumnLeavesBitmap = off;
  }

 
createHalloweenBitmap() {
  const size = 64;
  const off = document.createElement("canvas");
  off.width = size;
  off.height = size;
  const c = off.getContext("2d");

  const centerX = size / 2;
  const centerY = size / 2;

  c.clearRect(0, 0, size, size);

  c.save();
  c.translate(centerX, centerY);

  const ghostSize = size * 0.7;

  // 帽子の描画
  c.fillStyle = "#8b4789"; // 紫色の帽子
  
  // 帽子のつば
  c.beginPath();
  c.ellipse(0, -ghostSize * 0.45, ghostSize * 0.45, ghostSize * 0.08, 0, 0, Math.PI * 2);
  c.fill();
  
  // 帽子の円錐部分
  c.beginPath();
  c.moveTo(-ghostSize * 0.25, -ghostSize * 0.45);
  c.quadraticCurveTo(-ghostSize * 0.15, -ghostSize * 0.7, 0, -ghostSize * 0.75);
  c.quadraticCurveTo(ghostSize * 0.15, -ghostSize * 0.7, ghostSize * 0.25, -ghostSize * 0.45);
  c.closePath();
  c.fill();
  
  // 帽子のリボン
  c.fillStyle = "#6a1b68";
  c.fillRect(-ghostSize * 0.25, -ghostSize * 0.48, ghostSize * 0.5, ghostSize * 0.08);

  // お化けの影
  c.shadowBlur = 10;
  c.shadowColor = "rgba(255, 255, 255, 0.5)";

  // お化けの体(白いグラデーション)
  const gradient = c.createRadialGradient(0, -ghostSize * 0.1, 0, 0, 0, ghostSize / 2);
  gradient.addColorStop(0, "#ffffff");
  gradient.addColorStop(0.7, "#f5f5f5");
  gradient.addColorStop(1, "#e0e0e0");

  c.fillStyle = gradient;
  
  // お化けの頭部(丸い部分)
  c.beginPath();
  c.arc(0, -ghostSize * 0.15, ghostSize * 0.35, 0, Math.PI * 2);
  c.fill();

  // お化けの体部分(下に広がる)
  c.beginPath();
  c.moveTo(-ghostSize * 0.35, -ghostSize * 0.15);
  c.lineTo(-ghostSize * 0.35, ghostSize * 0.15);
  
  // 波打つ裾
  const waveCount = 4;
  for (let i = 0; i < waveCount; i++) {
    const x1 = -ghostSize * 0.35 + (ghostSize * 0.7 / waveCount) * i;
    const x2 = -ghostSize * 0.35 + (ghostSize * 0.7 / waveCount) * (i + 0.5);
    const x3 = -ghostSize * 0.35 + (ghostSize * 0.7 / waveCount) * (i + 1);
    c.quadraticCurveTo(x2, ghostSize * 0.25, x3, ghostSize * 0.15);
  }
  
  c.lineTo(ghostSize * 0.35, -ghostSize * 0.15);
  c.closePath();
  c.fill();

  c.shadowBlur = 0;

  // 目(黒い点)
  c.fillStyle = "#000000";
  
  // 左目
  c.beginPath();
  c.arc(-ghostSize * 0.15, -ghostSize * 0.15, ghostSize * 0.08, 0, Math.PI * 2);
  c.fill();
  
  // 右目
  c.beginPath();
  c.arc(ghostSize * 0.15, -ghostSize * 0.15, ghostSize * 0.08, 0, Math.PI * 2);
  c.fill();

  // 口(笑顔)
  c.beginPath();
  c.arc(0, -ghostSize * 0.05, ghostSize * 0.15, 0, Math.PI, false);
  c.lineWidth = ghostSize * 0.04;
  c.strokeStyle = "#000000";
  c.stroke();

  // 手(左右)
  c.fillStyle = gradient;
  
  // 左手
  c.beginPath();
  c.ellipse(-ghostSize * 0.4, 0, ghostSize * 0.12, ghostSize * 0.08, -Math.PI / 6, 0, Math.PI * 2);
  c.fill();
  
  // 右手
  c.beginPath();
  c.ellipse(ghostSize * 0.4, 0, ghostSize * 0.12, ghostSize * 0.08, Math.PI / 6, 0, Math.PI * 2);
  c.fill();

  c.restore();

  this.halloweenBitmap = off;
}



  initSnowflakes() {
    const area = this.width * this.height;
    const scale = Math.min(Math.max(area / OverlayAnimation.BASE_AREA, 0.3), 1.0);
    const opts = this.snowOpts;
    const count = Math.round(opts.count * scale);

    const flakes = this.flakes;
    flakes.length = 0;

    for (let i = 0; i < count; i++) {
      const size = this._randSnowSize();
      flakes.push({
        x: Math.random() * this.width,
        y: Math.random() * this.height,
        size,
        vy: OverlayAnimation.rand(opts.speedMin, opts.speedMax),
        vx: OverlayAnimation.rand(opts.windMin, opts.windMax),
        rot: OverlayAnimation.rand(0, Math.PI * 2),
        vrot: OverlayAnimation.rand(-0.02, 0.02)
      });
    }
  }

  initSakuraPetals() {
    const area = this.width * this.height;
    const scale = Math.min(Math.max(area / OverlayAnimation.BASE_AREA, 0.3), 1.0);
    const count = Math.round(20 * scale);

    const flakes = this.flakes;
    flakes.length = 0;

    for (let i = 0; i < count; i++) {
      const size = this._randSakuraSize();
      flakes.push({
        x: Math.random() * this.width,
        y: Math.random() * this.height,
        size,
        vy: OverlayAnimation.rand(0.3, 0.6),
        vx: OverlayAnimation.rand(-0.3, 0.3),
        rot: OverlayAnimation.rand(0, Math.PI * 2),
        vrot: OverlayAnimation.rand(-0.06, 0.03),
        swing: OverlayAnimation.rand(0, Math.PI * 2),
        swingSpeed: OverlayAnimation.rand(0.01, 0.03)
      });
    }
  }

  initHearts() {
    this.flakes = [];
    const count = this.heartCount || 12;  // ハートの数
    
    for (let i = 0; i < count; i++) {
      this.flakes.push({
        x: Math.random() * this.width,
        y: Math.random() * this.height,
        vx: 0,
        vy: -(Math.random() * 0.5 + 0.2),  // マイナスを追加で逆行
        rot: Math.random() * Math.PI * 2,  // 初期回転角度
        vrot: (Math.random() - 0.5) * 0.05,  // 回転速度（-0.025〜0.025）
        size: Math.random() * 10 + 40,  // ハートの大きさ プラス+最低値
        pulse:  0,  // 脈動を無効化
        pulseSpeed:  0  // 脈動速度を0に
      });
    }
  }

  initRosePetals() {
    const area = this.width * this.height;
    const scale = Math.min(Math.max(area / OverlayAnimation.BASE_AREA, 0.3), 1.0);
    const count = Math.round(70 * scale);

    const flakes = this.flakes;
    flakes.length = 0;

    for (let i = 0; i < count; i++) {
      const size = this._randRoseSize();
      flakes.push({
        x: Math.random() * this.width,
        y: Math.random() * this.height,
        size,
        vy: OverlayAnimation.rand(0.4, 0.7),
        vx: OverlayAnimation.rand(-0.2, 0.2),
        rot: OverlayAnimation.rand(0, Math.PI * 2),
        vrot: OverlayAnimation.rand(-0.05, 0.05),
        swing: OverlayAnimation.rand(0, Math.PI * 2),
        swingSpeed: OverlayAnimation.rand(0.015, 0.025)
      });
    }
  }

  initHydrangeaPetals() {
    const area = this.width * this.height;
    const scale = Math.min(Math.max(area / OverlayAnimation.BASE_AREA, 0.3), 1.0);
    const count = Math.round(50 * scale);

    const flakes = this.flakes;
    flakes.length = 0;

    for (let i = 0; i < count; i++) {
      const size = this._randHydrangeaSize();
      flakes.push({
        x: Math.random() * this.width,
        y: Math.random() * this.height,
        size,
        vy: OverlayAnimation.rand(0.3, 0.5),
        vx: OverlayAnimation.rand(-0.25, 0.25),
        rot: OverlayAnimation.rand(0, Math.PI * 2),
        vrot: OverlayAnimation.rand(-0.04, 0.04),
        swing: OverlayAnimation.rand(0, Math.PI * 2),
        swingSpeed: OverlayAnimation.rand(0.01, 0.02)
      });
    }
  }

  initMargueritePetals() {
    const area = this.width * this.height;
    const scale = Math.min(Math.max(area / OverlayAnimation.BASE_AREA, 0.3), 1.0);
    const count = Math.round(60 * scale);

    const flakes = this.flakes;
    flakes.length = 0;

    for (let i = 0; i < count; i++) {
      const size = this._randMargueriteSize();
      flakes.push({
        x: Math.random() * this.width,
        y: Math.random() * this.height,
        size,
        vy: OverlayAnimation.rand(0.35, 0.6),
        vx: OverlayAnimation.rand(-0.2, 0.2),
        rot: OverlayAnimation.rand(0, Math.PI * 2),
        vrot: OverlayAnimation.rand(-0.05, 0.05),
        swing: OverlayAnimation.rand(0, Math.PI * 2),
        swingSpeed: OverlayAnimation.rand(0.012, 0.025)
      });
    }
  }

  initAutumnLeaves() {
    const area = this.width * this.height;
    const scale = Math.min(Math.max(area / OverlayAnimation.BASE_AREA, 0.3), 1.0);
    const count = Math.round(65 * scale);

    const flakes = this.flakes;
    flakes.length = 0;

    for (let i = 0; i < count; i++) {
      const size = this._randAutumnLeavesSize();
      flakes.push({
        x: Math.random() * this.width,
        y: Math.random() * this.height,
        size,
        vy: OverlayAnimation.rand(0.5, 0.9),
        vx: OverlayAnimation.rand(-0.4, 0.4),
        rot: OverlayAnimation.rand(0, Math.PI * 2),
        vrot: OverlayAnimation.rand(-0.08, 0.08),
        swing: OverlayAnimation.rand(0, Math.PI * 2),
        swingSpeed: OverlayAnimation.rand(0.02, 0.04)
      });
    }
  }

  initHalloweenItems() {
    const area = this.width * this.height;
    const scale = Math.min(Math.max(area / OverlayAnimation.BASE_AREA, 0.3), 1.0);
    const count = Math.round(45 * scale);

    const flakes = this.flakes;
    flakes.length = 0;

    for (let i = 0; i < count; i++) {
      const size = this._randHalloweenSize();
      flakes.push({
        x: Math.random() * this.width,
        y: Math.random() * this.height,
        size,
        vy: OverlayAnimation.rand(0.4, 0.7),
        vx: OverlayAnimation.rand(-0.3, 0.3),
        rot: OverlayAnimation.rand(0, Math.PI * 2),
        vrot: OverlayAnimation.rand(-0.06, 0.06),
        swing: OverlayAnimation.rand(0, Math.PI * 2),
        swingSpeed: OverlayAnimation.rand(0.015, 0.03)
      });
    }
  }

  drawInitialFrame() {
    const ctx = this.ctx;
    const width = this.width, height = this.height;

    if (this.mode === "fireworks") {
      ctx.clearRect(0, 0, width, height);
      ctx.save();
      ctx.shadowBlur = 20;
      ctx.shadowColor = OverlayAnimation.FIRE_COLORS[0];
      ctx.fillStyle = OverlayAnimation.FIRE_COLORS[0];
      ctx.beginPath();
      ctx.arc(width * 0.5, height - 30, 2.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    } else if (this.mode === "snow") {
      ctx.clearRect(0, 0, width, height);
      const count = 14;
      for (let i = 0; i < count; i++) {
        const x = ((i * 87) % width) + 20;
        const y = ((i * 209) % height) + 20;
        const size = 10 + (i % 5) * 4;

        ctx.save();
        ctx.globalAlpha = 0.8;
        ctx.translate(x, y);
        ctx.rotate((i % 6) * 0.4 + 0.1);

        if (this.snowBitmap) {
          ctx.drawImage(
            this.snowBitmap,
            -size / 2,
            -size / 2,
            size,
            size
          );
        }
        ctx.restore();
      }
    } else if (this.mode === "sakura") {
      ctx.clearRect(0, 0, width, height);
      const count = 12;
      for (let i = 0; i < count; i++) {
        const x = ((i * 97) % width) + 20;
        const y = ((i * 179) % height) + 20;
        const size = 12 + (i % 5) * 5;

        ctx.save();
        ctx.globalAlpha = 0.7;
        ctx.translate(x, y);
        ctx.rotate((i % 8) * 0.5 + 0.2);

        if (this.sakuraBitmap) {
          ctx.drawImage(
            this.sakuraBitmap,
            -size / 2,
            -size / 2,
            size,
            size
          );
        }
        ctx.restore();
      }
    } else if (this.mode === "heart") {
      ctx.clearRect(0, 0, width, height);
      const count = 10;
      for (let i = 0; i < count; i++) {
        const x = ((i * 107) % width) + 20;
        const y = ((i * 193) % height) + 20;
        const size = 10 + (i % 5) * 4;

        ctx.save();
        ctx.globalAlpha = 0.6;
        ctx.translate(x, y);
        ctx.rotate((i % 6) * 0.6);

        if (this.heartBitmap) {
          ctx.drawImage(
            this.heartBitmap,
            -size / 2,
            -size / 2,
            size,
            size
          );
        }
        ctx.restore();
      }
    } else if (this.mode === "rose") {
      ctx.clearRect(0, 0, width, height);
      const count = 11;
      for (let i = 0; i < count; i++) {
        const x = ((i * 103) % width) + 20;
        const y = ((i * 187) % height) + 20;
        const size = 14 + (i % 5) * 5;

        ctx.save();
        ctx.globalAlpha = 0.75;
        ctx.translate(x, y);
        ctx.rotate((i % 7) * 0.5 + 0.15);

        if (this.roseBitmap) {
          ctx.drawImage(
            this.roseBitmap,
            -size / 2,
            -size / 2,
            size,
            size
          );
        }
        ctx.restore();
      }
    } else if (this.mode === "hydrangea") {
      ctx.clearRect(0, 0, width, height);
      const count = 9;
      for (let i = 0; i < count; i++) {
        const x = ((i * 113) % width) + 25;
        const y = ((i * 197) % height) + 25;
        const size = 18 + (i % 5) * 6;

        ctx.save();
        ctx.globalAlpha = 0.8;
        ctx.translate(x, y);
        ctx.rotate((i % 6) * 0.55);

        if (this.hydrangeaBitmap) {
          ctx.drawImage(
            this.hydrangeaBitmap,
            -size / 2,
            -size / 2,
            size,
            size
          );
        }
        ctx.restore();
      }
    } else if (this.mode === "marguerite") {
      ctx.clearRect(0, 0, width, height);
      const count = 10;
      for (let i = 0; i < count; i++) {
        const x = ((i * 109) % width) + 22;
        const y = ((i * 191) % height) + 22;
        const size = 16 + (i % 5) * 5;

        ctx.save();
        ctx.globalAlpha = 0.75;
        ctx.translate(x, y);
        ctx.rotate((i % 7) * 0.48);

        if (this.margueriteBitmap) {
          ctx.drawImage(
            this.margueriteBitmap,
            -size / 2,
            -size / 2,
            size,
            size
          );
        }
        ctx.restore();
      }
    } else if (this.mode === "autumn_leaves") {
      ctx.clearRect(0, 0, width, height);
      const count = 11;
      for (let i = 0; i < count; i++) {
        const x = ((i * 101) % width) + 20;
        const y = ((i * 181) % height) + 20;
        const size = 13 + (i % 5) * 5;

        ctx.save();
        ctx.globalAlpha = 0.8;
        ctx.translate(x, y);
        ctx.rotate((i % 8) * 0.6 + 0.2);

        if (this.autumnLeavesBitmap) {
          ctx.drawImage(
            this.autumnLeavesBitmap,
            -size / 2,
            -size / 2,
            size,
            size
          );
        }
        ctx.restore();
      }
    } else if (this.mode === "halloween") {
      ctx.clearRect(0, 0, width, height);
      const count = 8;
      for (let i = 0; i < count; i++) {
        const x = ((i * 117) % width) + 25;
        const y = ((i * 199) % height) + 25;
        const size = 20 + (i % 5) * 6;

        ctx.save();
        ctx.globalAlpha = 0.85;
        ctx.translate(x, y);
        ctx.rotate((i % 6) * 0.52);

        if (this.halloweenBitmap) {
          ctx.drawImage(
            this.halloweenBitmap,
            -size / 2,
            -size / 2,
            size,
            size
          );
        }
        ctx.restore();
      }
    } else {
      ctx.clearRect(0, 0, width, height);
    }
  }

  animate = () => {
    if (!this.animatingStarted) return;

    const ctx = this.ctx;
    ctx.clearRect(0, 0, this.width, this.height);

    if (this.mode === "fireworks") {
      if (Math.random() < 0.02) this.spawnRocket();

      for (let i = this.rockets.length - 1; i >= 0; i--) {
        const r = this.rockets[i];
        r.x += r.vx * this.fireworkSpeed;
        r.y += r.vy * this.fireworkSpeed;
        r.vy += r.ay * this.fireworkSpeed;

        ctx.save();
        ctx.shadowBlur = 20;
        ctx.shadowColor = r.color;
        ctx.fillStyle = r.color;
        ctx.beginPath();
        ctx.arc(r.x, r.y, 2.2, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        if (r.y <= r.far || r.vy >= -2) {
          this.explode(r.x, r.y, r.color);
          this.rockets.splice(i, 1);
        }
      }
      for (let i = this.particles.length - 1; i >= 0; i--) {
        const p = this.particles[i];
        p.x += p.vx * this.fireworkSpeed;
        p.y += p.vy * this.fireworkSpeed;
        p.vy += p.ay * this.fireworkSpeed;
        p.life--;
        p.alpha = p.life / 60;
        if (p.life <= 0 || p.alpha <= 0) {
          this.particles.splice(i, 1);
          continue;
        }
        ctx.save();
        ctx.globalAlpha = p.alpha;
        ctx.shadowBlur = 25;
        ctx.shadowColor = p.color;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    }

    if (this.mode === "snow") {
      if (!this.flakes.length) this.initSnowflakes();
      const flakes = this.flakes;
      const width = this.width, height = this.height, snowSpeed = this.snowSpeed;
      const bmp = this.snowBitmap;

      for (let i = 0, len = flakes.length; i < len; i++) {
        const f = flakes[i];
        f.x += f.vx * snowSpeed;
        f.y += f.vy * snowSpeed;
        f.rot += f.vrot * snowSpeed;

        if (f.y > height + 50) f.y = -50;
        if (f.x > width + 50) f.x = -50;
        if (f.x < -50) f.x = width + 50;

        if (!bmp) continue;

        ctx.save();
        ctx.translate(f.x, f.y);
        ctx.rotate(f.rot);
        ctx.drawImage(
          bmp,
          -f.size / 2,
          -f.size / 2,
          f.size,
          f.size
        );
        ctx.restore();
      }
    }

    if (this.mode === "sakura") {
      if (!this.flakes.length) this.initSakuraPetals();
      const flakes = this.flakes;
      const width = this.width, height = this.height, sakuraSpeed = this.sakuraSpeed;
      const bmp = this.sakuraBitmap;

      for (let i = 0, len = flakes.length; i < len; i++) {
        const f = flakes[i];
        
        f.swing += f.swingSpeed * sakuraSpeed;
        const swingOffset = Math.sin(f.swing) * 30;
        
        f.x += (f.vx + swingOffset * 0.02) * sakuraSpeed;
        f.y += f.vy * sakuraSpeed;
        f.rot += f.vrot * sakuraSpeed;

        if (f.y > height + 50) {
          f.y = -50;
          f.x = Math.random() * width;
        }
        if (f.x > width + 50) f.x = -50;
        if (f.x < -50) f.x = width + 50;

        if (!bmp) continue;

        ctx.save();
        ctx.globalAlpha = 0.85;
        ctx.translate(f.x, f.y);
        ctx.rotate(f.rot);
        ctx.drawImage(
          bmp,
          -f.size / 2,
          -f.size / 2,
          f.size,
          f.size
        );
        ctx.restore();
      }
    }

    if (this.mode === "heart") {
      if (!this.flakes.length) this.initHearts();
      const flakes = this.flakes;
      const width = this.width, height = this.height, heartSpeed = this.heartSpeed;
      const bmp = this.heartBitmap;

      for (let i = 0, len = flakes.length; i < len; i++) {
        const f = flakes[i];
        
        f.pulse += f.pulseSpeed * heartSpeed;
        const pulseScale = 1 + Math.sin(f.pulse) * 0.15;
        
        f.x += f.vx * heartSpeed;
        f.y += f.vy * heartSpeed;
        f.rot += f.vrot * heartSpeed;

        if (f.y > height + 50) {
          f.y = -50;
          f.x = Math.random() * width;
        }
        if (f.x > width + 50) f.x = -50;
        if (f.x < -50) f.x = width + 50;

        if (!bmp) continue;

        ctx.save();
        ctx.globalAlpha = 0.8;
        ctx.translate(f.x, f.y);
        ctx.rotate(f.rot);
        const scaledSize = f.size * pulseScale;
        ctx.drawImage(
          bmp,
          -scaledSize / 2,
          -scaledSize / 2,
          scaledSize,
          scaledSize
        );
        ctx.restore();
      }
    }

    if (this.mode === "rose") {
      if (!this.flakes.length) this.initRosePetals();
      const flakes = this.flakes;
      const width = this.width, height = this.height, roseSpeed = this.roseSpeed;
      const bmp = this.roseBitmap;

      for (let i = 0, len = flakes.length; i < len; i++) {
        const f = flakes[i];
        
        f.swing += f.swingSpeed * roseSpeed;
        const swingOffset = Math.sin(f.swing) * 25;
        
        f.x += (f.vx + swingOffset * 0.02) * roseSpeed;
        f.y += f.vy * roseSpeed;
        f.rot += f.vrot * roseSpeed;

        if (f.y > height + 50) {
          f.y = -50;
          f.x = Math.random() * width;
        }
        if (f.x > width + 50) f.x = -50;
        if (f.x < -50) f.x = width + 50;

        if (!bmp) continue;

        ctx.save();
        ctx.globalAlpha = 0.8;
        ctx.translate(f.x, f.y);
        ctx.rotate(f.rot);
        ctx.drawImage(
          bmp,
          -f.size / 2,
          -f.size / 2,
          f.size,
          f.size
        );
        ctx.restore();
      }
    }

    if (this.mode === "hydrangea") {
      if (!this.flakes.length) this.initHydrangeaPetals();
      const flakes = this.flakes;
      const width = this.width, height = this.height, hydrangeaSpeed = this.hydrangeaSpeed;
      const bmp = this.hydrangeaBitmap;

      for (let i = 0, len = flakes.length; i < len; i++) {
        const f = flakes[i];
        
        f.swing += f.swingSpeed * hydrangeaSpeed;
        const swingOffset = Math.sin(f.swing) * 20;
        
        f.x += (f.vx + swingOffset * 0.015) * hydrangeaSpeed;
        f.y += f.vy * hydrangeaSpeed;
        f.rot += f.vrot * hydrangeaSpeed;

        if (f.y > height + 50) {
          f.y = -50;
          f.x = Math.random() * width;
        }
        if (f.x > width + 50) f.x = -50;
        if (f.x < -50) f.x = width + 50;

        if (!bmp) continue;

        ctx.save();
        ctx.globalAlpha = 0.85;
        ctx.translate(f.x, f.y);
        ctx.rotate(f.rot);
        ctx.drawImage(
          bmp,
          -f.size / 2,
          -f.size / 2,
          f.size,
          f.size
        );
        ctx.restore();
      }
    }

    if (this.mode === "marguerite") {
      if (!this.flakes.length) this.initMargueritePetals();
      const flakes = this.flakes;
      const width = this.width, height = this.height, margueriteSpeed = this.margueriteSpeed;
      const bmp = this.margueriteBitmap;

      for (let i = 0, len = flakes.length; i < len; i++) {
        const f = flakes[i];
        
        f.swing += f.swingSpeed * margueriteSpeed;
        const swingOffset = Math.sin(f.swing) * 28;
        
        f.x += (f.vx + swingOffset * 0.018) * margueriteSpeed;
        f.y += f.vy * margueriteSpeed;
        f.rot += f.vrot * margueriteSpeed;

        if (f.y > height + 50) {
          f.y = -50;
          f.x = Math.random() * width;
        }
        if (f.x > width + 50) f.x = -50;
        if (f.x < -50) f.x = width + 50;

        if (!bmp) continue;

        ctx.save();
        ctx.globalAlpha = 0.8;
        ctx.translate(f.x, f.y);
        ctx.rotate(f.rot);
        ctx.drawImage(
          bmp,
          -f.size / 2,
          -f.size / 2,
          f.size,
          f.size
        );
        ctx.restore();
      }
    }

    if (this.mode === "autumn_leaves") {
      if (!this.flakes.length) this.initAutumnLeaves();
      const flakes = this.flakes;
      const width = this.width, height = this.height, autumnLeavesSpeed = this.autumnLeavesSpeed;
      const bmp = this.autumnLeavesBitmap;

      for (let i = 0, len = flakes.length; i < len; i++) {
        const f = flakes[i];
        
        f.swing += f.swingSpeed * autumnLeavesSpeed;
        const swingOffset = Math.sin(f.swing) * 35;
        
        f.x += (f.vx + swingOffset * 0.025) * autumnLeavesSpeed;
        f.y += f.vy * autumnLeavesSpeed;
        f.rot += f.vrot * autumnLeavesSpeed;

        if (f.y > height + 50) {
          f.y = -50;
          f.x = Math.random() * width;
        }
        if (f.x > width + 50) f.x = -50;
        if (f.x < -50) f.x = width + 50;

        if (!bmp) continue;

        ctx.save();
        ctx.globalAlpha = 0.85;
        ctx.translate(f.x, f.y);
        ctx.rotate(f.rot);
        ctx.drawImage(
          bmp,
          -f.size / 2,
          -f.size / 2,
          f.size,
          f.size
        );
        ctx.restore();
      }
    }

    if (this.mode === "halloween") {
      if (!this.flakes.length) this.initHalloweenItems();
      const flakes = this.flakes;
      const width = this.width, height = this.height, halloweenSpeed = this.halloweenSpeed;
      const bmp = this.halloweenBitmap;

      for (let i = 0, len = flakes.length; i < len; i++) {
        const f = flakes[i];
        
        f.swing += f.swingSpeed * halloweenSpeed;
        const swingOffset = Math.sin(f.swing) * 30;
        
        f.x += (f.vx + swingOffset * 0.02) * halloweenSpeed;
        f.y += f.vy * halloweenSpeed;
        f.rot += f.vrot * halloweenSpeed;

        if (f.y > height + 50) {
          f.y = -50;
          f.x = Math.random() * width;
        }
        if (f.x > width + 50) f.x = -50;
        if (f.x < -50) f.x = width + 50;

        if (!bmp) continue;

        ctx.save();
        ctx.globalAlpha = 0.9;
        ctx.translate(f.x, f.y);
        ctx.rotate(f.rot);
        ctx.drawImage(
          bmp,
          -f.size / 2,
          -f.size / 2,
          f.size,
          f.size
        );
        ctx.restore();
      }
    }

    requestAnimationFrame(this.animate);
  };
}

if (!customElements.get("overlay-animation")) {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      customElements.define("overlay-animation", OverlayAnimation);
    });
  } else {
    setTimeout(() => {
      customElements.define("overlay-animation", OverlayAnimation);
    }, 0);
  }
}